import { useEffect, useState, useRef } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { fetch, infiniteFetch } from "../enums/queryFunction/Fetch";
import useUser from "./useUser";
import { getToken, logout } from "../utils/Functions";
import { useNavigate } from "react-router-dom";
import { LOGIN } from "../enums/UrlPaths";
import logger from "../utils/logger";
import api from "../utils/api/authApi";

function useFetchWithoutHeader(url) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);


  const fetchData = async () => {
    logger.debug("[useFetchWithoutHeader] Fetching — url:", url);
    setIsLoading(true);
    try {
      api
        .get(url)
        .then((res) => {
          logger.debug("[useFetchWithoutHeader] Success — url:", url, "| data:", res.data);
          setData(res.data);
          setIsLoading(false);
        })
        .catch((e) => {
          logger.error("[useFetchWithoutHeader] Axios error — url:", url, "| error:", e);
          setError(true);
        });
    } catch (e) {
      logger.error("[useFetchWithoutHeader] Caught exception — url:", url, "| error:", e);
      if (e.response) {
        setError(e.response.data);
      } else if (e.request) {
        setError(e.request);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    logger.debug("[useFetchWithoutHeader] useEffect — url changed, fetching:", url);
    fetchData();
    return () => {
      logger.debug("[useFetchWithoutHeader] useEffect cleanup — resetting state for url:", url);
      setData(null);
      setError(false);
      setIsLoading(false);
    };
  }, [url]);

  return { data, isLoading, error };
}

// 401s are handled once, centrally, by PrivateRoute's api response
// interceptor (refresh + retry, logout only if that fails). These hooks used
// to additionally navigate(-1) on every 401, bouncing the user off the page.
function useFetch(url, id, key, isEnabled = true, staleTime = Infinity) {
  const { isLoading: loadingUser } = useUser();
  const queryKey = Array.isArray(key) ? key : [key];
  const enabled = Boolean(!loadingUser && isEnabled && url);
  
  logger.debug("[useFetch] Init — url:", url, "| id:", id, "| key:", queryKey, "| enabled:", enabled, "| loadingUser:", loadingUser);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey,
    queryFn: () => {
      logger.debug("[useFetch] queryFn firing — url:", url, "| id:", id);
      return fetch(url, id);
    },
    staleTime,
    enabled,
    retry: (failureCount, error) => {
      // 401: handled by PrivateRoute. 403: retrying a refusal can't succeed.
      if ([401, 403].includes(error?.response?.status)) return false;
      return failureCount < 2;
    },
  });

  return {
    data,
    isLoading: isLoading || loadingUser,
    error,
    refetch,
  };
}

export function useInfiniteFetch(
  url,
  id,
  key,
  isEnabled = true,
  staleTime = Infinity,
) {
  const navigate = useNavigate();
  const { isLoading: loadingUser } = useUser();

  const queryKey = Array.isArray(key) ? key : [key];
  const enabled = Boolean(!loadingUser && isEnabled);

  logger.debug("[useInfiniteFetch] Init — url:", url, "| id:", id, "| key:", queryKey, "| enabled:", enabled, "| loadingUser:", loadingUser);


  const { data, isLoading, error, refetch, status } = useInfiniteQuery({
    queryKey: Array.isArray(key) ? key : [key],
    queryFn: (pageParam) => infiniteFetch(url, id, pageParam),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.nextPage,
    staleTime,
    enabled: Boolean(!loadingUser && isEnabled),
    retry: (failureCount, error) => {
      // 401: handled by PrivateRoute. 403: retrying a refusal can't succeed.
      if ([401, 403].includes(error?.response?.status)) return false;
      return failureCount < 2;
    },
  });

  useEffect(() => {
    if (error?.message === "Network Error") {
      logout();
      navigate(LOGIN);
      return Promise.reject(error);
    }
  }, [error, navigate]);

  return { data, isLoading, error, refetch, status };
}

function usePostFilter(url, dto, key, isEnabled = true, staleTime = Infinity) {
  const { isLoading: loadingUser } = useUser();

  const queryKey = Array.isArray(key) ? key : [key];
  const enabled = Boolean(!loadingUser && isEnabled);

  logger.debug("[usePostFilter] Init — url:", url, "| key:", queryKey, "| enabled:", enabled, "| dto:", dto);


  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: async () => {
      logger.debug("[usePostFilter] queryFn firing — url:", url, "| dto:", dto);
      const token = getToken();
      if (!token || !url) {
        logger.warn("[usePostFilter] queryFn — missing token or url, returning null | token:", !!token, "| url:", url);
        return null;
      }
      logger.debug("[usePostFilter] queryFn — posting to:", url);
      const response = await api.post(url, dto);
      logger.debug("[usePostFilter] queryFn — response received:", response.data);
      return response.data;
    },
    staleTime,
    enabled,
  });

  useEffect(() => {
    if (error) {
      logger.error("[usePostFilter] Query error — url:", url, "| error:", error);
    }
  }, [error]);

  useEffect(() => {
    if (data !== undefined) {
      logger.debug("[usePostFilter] Data received — url:", url, "| data:", data);
    }
  }, [data]);

  return { data, isLoading, error };
}

export { useFetch, useFetchWithoutHeader, usePostFilter };